/**
 * Layout container properties shared by blocks that can contain children.
 *
 * Used by: All container blocks (plays, formations, documents, slides, sections, rows, columns)
 *
 * NOTE: This module provides TypeScript interfaces that align with the Zod schemas
 * defined in src/types/block-properties.ts. The Zod schemas are the source of truth
 * for runtime validation.
 */

import type { LayoutProperties } from '@/types/BlockProperties'

// Re-export the Zod-inferred type for convenience
export type { LayoutProperties }

/**
 * How a container arranges its children.
 * - free: Children use their own position_x, position_y (PowerPoint-style)
 * - row: Horizontal flow by display_order
 * - column: Vertical flow by display_order
 * - grid: Grid placement by display_order
 */
export type LayoutMode = 'free' | 'row' | 'column' | 'grid'

/**
 * Alignment options for layout containers.
 */
export type LayoutAlign = 'start' | 'center' | 'end' | 'stretch' | 'space-between'

/**
 * Layout container properties for blocks that can contain children.
 * Uses flexbox/grid-like semantics for child positioning.
 *
 * Uses camelCase to match TypeScript conventions and the JSONB properties column.
 * Database columns use snake_case (converted during persistence).
 *
 * @deprecated Use LayoutProperties from block-properties.ts instead.
 * This interface is kept for backward compatibility with existing code.
 */
export interface LayoutContainerProperties {
	/** How children are arranged (NULL = inherit or use free positioning) */
	layout_mode: LayoutMode | null
	/** Gap between children in layout flow (pixels/points) */
	layout_gap: number
	/** Padding inside the container */
	layout_padding: number
	/** Main axis alignment (row: horizontal, column: vertical) */
	layout_align_main: LayoutAlign
	/** Cross axis alignment */
	layout_align_cross: LayoutAlign
	/** Whether children wrap to new lines when container overflows */
	layout_wrap: boolean
	/** Grid columns (for grid layout mode) */
	layout_grid_columns: number | null
	/** Grid rows (for grid layout mode) */
	layout_grid_rows: number | null
}

/**
 * Default layout properties for new container blocks.
 */
export const DEFAULT_LAYOUT: LayoutContainerProperties = {
	layout_mode: null,
	layout_gap: 0,
	layout_padding: 0,
	layout_align_main: 'start',
	layout_align_cross: 'start',
	layout_wrap: false,
	layout_grid_columns: null,
	layout_grid_rows: null,
}

/**
 * Default layout properties in camelCase format for JSONB storage.
 * Use this when creating new blocks with the properties column.
 */
export const DEFAULT_LAYOUT_PROPERTIES: NonNullable<LayoutProperties> = {
	mode: undefined,
	gap: 0,
	padding: 0,
	alignMain: 'start',
	alignCross: 'start',
	wrap: false,
	gridColumns: undefined,
	gridRows: undefined,
}

/**
 * Default layout properties for document containers.
 * Documents use column layout for vertical content flow.
 */
export const DEFAULT_DOCUMENT_LAYOUT: LayoutContainerProperties = {
	layout_mode: 'column',
	layout_gap: 8,
	layout_padding: 16,
	layout_align_main: 'start',
	layout_align_cross: 'stretch',
	layout_wrap: false,
	layout_grid_columns: null,
	layout_grid_rows: null,
}

/**
 * Default document layout in camelCase format for JSONB storage.
 */
export const DEFAULT_DOCUMENT_LAYOUT_PROPERTIES: NonNullable<LayoutProperties> = {
	mode: 'column',
	gap: 8,
	padding: 16,
	alignMain: 'start',
	alignCross: 'stretch',
	wrap: false,
	gridColumns: undefined,
	gridRows: undefined,
}

/**
 * Default layout properties for slides.
 * Slides use free positioning (PowerPoint-style).
 */
export const DEFAULT_SLIDE_LAYOUT: LayoutContainerProperties = {
	layout_mode: 'free',
	layout_gap: 0,
	layout_padding: 24,
	layout_align_main: 'start',
	layout_align_cross: 'start',
	layout_wrap: false,
	layout_grid_columns: null,
	layout_grid_rows: null,
}

/**
 * Default slide layout in camelCase format for JSONB storage.
 */
export const DEFAULT_SLIDE_LAYOUT_PROPERTIES: NonNullable<LayoutProperties> = {
	mode: 'free',
	gap: 0,
	padding: 24,
	alignMain: 'start',
	alignCross: 'start',
	wrap: false,
	gridColumns: undefined,
	gridRows: undefined,
}

/**
 * Default layout properties for canvas blocks (plays, formations, concepts).
 * Canvas blocks use free positioning with feet coordinate system.
 */
export const DEFAULT_CANVAS_LAYOUT: LayoutContainerProperties = {
	layout_mode: 'free',
	layout_gap: 0,
	layout_padding: 0,
	layout_align_main: 'start',
	layout_align_cross: 'start',
	layout_wrap: false,
	layout_grid_columns: null,
	layout_grid_rows: null,
}

/**
 * Default canvas layout in camelCase format for JSONB storage.
 */
export const DEFAULT_CANVAS_LAYOUT_PROPERTIES: NonNullable<LayoutProperties> = {
	mode: 'free',
	gap: 0,
	padding: 0,
	alignMain: 'start',
	alignCross: 'start',
	wrap: false,
	gridColumns: undefined,
	gridRows: undefined,
}

/**
 * Default layout properties for row containers.
 */
export const DEFAULT_ROW_LAYOUT_PROPERTIES: NonNullable<LayoutProperties> = {
	mode: 'row',
	gap: 8,
	padding: 0,
	alignMain: 'start',
	alignCross: 'stretch',
	wrap: false,
	gridColumns: undefined,
	gridRows: undefined,
}

/**
 * Default layout properties for column containers.
 */
export const DEFAULT_COLUMN_LAYOUT_PROPERTIES: NonNullable<LayoutProperties> = {
	mode: 'column',
	gap: 8,
	padding: 0,
	alignMain: 'start',
	alignCross: 'stretch',
	wrap: false,
	gridColumns: undefined,
	gridRows: undefined,
}
